/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { gql, MutationUpdaterFn, useMutation } from '@apollo/client'
import { JsonObject } from '@ts/json'
import { DynamicForm as DynamicFormType, DynamicFormSubmissionPayload } from '@ts/operations'
import { GraphQLError } from 'graphql'
import React, { useState } from 'react'
import { IChangeEvent } from 'react-jsonschema-form'

import { DEFAULT_SPINNER_SIZE } from '../../lib/fallback'
import LoadingSpinner from '../utils/LoadingSpinner'
import DynamicFormLayout, { DynamicFormLayoutProps } from './DynamicFormLayout'
import {
  mergeNestedPayloads,
  nestedObjectFromDotNotation,
  payloadFieldNameFromMutation,
  removeReadOnlyFields,
} from './utils'
import { formatPatterns, transformErrors } from './validation'

export type OnSuccessResponse = {
  data: JsonObject | null
  errors?: readonly GraphQLError[]
  message?: string | null
}

export type DynamicFormSubmissionPayloadMap = { [field: string]: DynamicFormSubmissionPayload }

const DynamicForm = (
  props: DynamicFormLayoutProps & {
    afterMutation?: MutationUpdaterFn
    authHeader?: JsonObject
    onFocus?: ((id: string, value: boolean | number | string | null) => void) | undefined
    onSuccess: (result: OnSuccessResponse) => void
    transformInput?: (input: JsonObject) => JsonObject
  } & Pick<DynamicFormType, 'id' | 'mutation'>
) => {
  const {
    id,
    authHeader = {},
    data: initialData = {},
    schema,
    uiSchema = {},
    mutation,
    onSuccess,
    children,
    transformInput,
    afterMutation,
    onFocus,
  } = props

  const mutationDoc = gql(mutation)
  const payloadField = payloadFieldNameFromMutation(mutationDoc)
  const [submitForm] = useMutation<{ [field: string]: DynamicFormSubmissionPayload }>(mutationDoc)
  const [formData, setFormData] = useState(initialData)
  const [loading, setLoading] = useState(false)

  const transformAndSubmit = async (event: IChangeEvent) => {
    const { formData: currentFormData } = event
    setFormData(currentFormData)
    setLoading(true)

    const submittableFormData = removeReadOnlyFields(currentFormData, uiSchema)
    const input = nestedObjectFromDotNotation(submittableFormData)

    const formInput = transformInput ? transformInput(input) : input
    const { data, errors } = await submitForm({
      context: authHeader,
      update: afterMutation,
      variables: { formId: id, formInput },
    })

    let responseData: JsonObject | null = null
    if (data) {
      responseData = mergeNestedPayloads(data, 'dynamicForm.data')
      setFormData(responseData)
    }

    onSuccess({
      data: responseData,
      errors,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      message: data && data[payloadField].successMessage,
    })
  }

  return (
    <DynamicFormLayout
      customFormats={formatPatterns}
      data={formData}
      schema={schema}
      showErrorList={false}
      transformErrors={transformErrors}
      uiSchema={uiSchema}
      onFocus={onFocus}
      onSubmit={transformAndSubmit}
    >
      {loading ? <LoadingSpinner size={DEFAULT_SPINNER_SIZE} /> : children}
    </DynamicFormLayout>
  )
}
export default DynamicForm
